import { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmberRounded'
import useTranslate from '@/hooks/use-translate'
import { getNf, nf } from '../nfTokens'

export interface NfFieldProps {
  label: string
  required?: boolean
  hint?: string | null
  tooltip?: string | null
  error?: string | null
  disabledReason?: string | null
  children: ReactNode
}

/**
 * Reproduit le pattern "NfField" de la maquette : label + pastille
 * REQUIS + tooltip optionnel, puis le champ, puis un hint
 * ou un message d'erreur (la raison de désactivation prime sur le hint
 * quand les deux existent).
 */
export const NfField: FC<NfFieldProps> = ({
  label,
  required,
  hint,
  tooltip,
  error,
  disabledReason,
  children,
}) => {
  const { t: tUpload } = useTranslate('upload')

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography sx={(theme) => ({ fontSize: 11, fontWeight: 600, color: getNf(theme).ink2 })}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            slotProps={{ tooltip: { sx: { maxWidth: 280, fontSize: 11 } } }}
          >
            <Box
              component="span"
              sx={(theme) => ({ display: 'inline-flex', color: getNf(theme).muted2, cursor: 'help' })}
              aria-label={tooltip}
            >
              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            </Box>
          </Tooltip>
        )}
        {required && (
          <Box
            component="span"
            sx={(theme) => {
              const t = getNf(theme)
              return {
                fontSize: 8.5,
                fontWeight: 700,
                letterSpacing: '.5px',
                color: t.requiredText,
                bgcolor: t.requiredBg,
                px: '6px',
                py: '2px',
                borderRadius: `${nf.radiusPill}px`,
                flexShrink: 0,
              }
            }}
          >
            {tUpload('nfRequired')}
          </Box>
        )}
      </Stack>
      {children}
      {error ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <WarningAmberIcon sx={(theme) => ({ fontSize: 11, color: getNf(theme).danger })} />
          <Typography sx={(theme) => ({ fontSize: 10, color: getNf(theme).danger })}>
            {error}
          </Typography>
        </Stack>
      ) : (
        (disabledReason || hint) && (
          <Typography sx={(theme) => ({ fontSize: 10, color: getNf(theme).muted })}>
            {disabledReason || hint}
          </Typography>
        )
      )}
    </Stack>
  )
}

export default NfField
